import {
  useCompletePatientExercise,
  useDeleteExerciseSettingById,
  useGetAllPatientExercisesByID,
} from '_api';
import { AddExerciseToPatientPayload, ContentSettingV2, Exercise } from '_api/types';
import { ExerciseRouteMap } from '_constants';
import { ThumbnailView } from 'components/atoms';
import { ActionsFlexBox, PatientProfileTable } from 'components/molecules';
import {
  AddExerciseToPatientProfileModal,
  EditExerciseToPatientProfileModal,
} from 'components/organisms/modals';
import dayjs from 'dayjs';
import { MRT_ColumnDef } from 'material-react-table';
import React, { useState, useMemo } from 'react';
import { useNavigateTo } from 'utils';

import { ProfileTableProps } from './types';

export const PatientExerciseTable = ({ patientId }: ProfileTableProps) => {
  const navigate = useNavigateTo();
  const [open, setOpen] = useState(false);
  const [editExercise, setEditExercise] = useState<
    AddExerciseToPatientPayload | undefined
  >(undefined);
  const [editExerciseLabel, setEditExerciseLabel] = useState<string>('');
  const handleCloseModal = () => {
    setOpen(false);
  };

  const { data: exercises, isLoading: isExerciseLoading } = useGetAllPatientExercisesByID(
    { patientId },
  );

  const activePatientExercisesById = useMemo(() => {
    return exercises ? exercises.filter((x) => x.status === 'ACTIVE') : [];
  }, [exercises]);
  const { mutateAsync: deleteExercise } = useDeleteExerciseSettingById();
  const { mutateAsync: completeExercise } = useCompletePatientExercise();

  const handleDeleteExerciseOnClick = (id: number) => {
    deleteExercise({ id });
  };

  const handleEditExerciseOnClick = (exercise: ContentSettingV2<Exercise>) => {
    const { content, description, therapyType, schedules, instructionList, ...data } =
      exercise;
    const active = schedules?.[schedules.length - 1];
    setEditExerciseLabel(content.title);
    setEditExercise({
      contentId: content.id,
      patientId: patientId,
      perWeek: active?.perWeek ?? 1,
      perDay: active?.perDay ?? 1,
      schedule: active?.schedule ?? [],
      reps: active?.reps ?? 1,
      sets: active?.sets ?? 1,
      schedules,
      therapyType: therapyType?.name,
      instructionList,
      description: description ?? content.description,
      ...data,
    });
  };

  const handleOnAddClick = () => {
    setOpen(true);
  };

  const handleCompleteOnClick = (id: number) => {
    const payload = {
      patientContentId: id,
      completedDate: dayjs().format('YYYY-MM-DD'),
    };
    completeExercise(payload);
  };

  const columns = useMemo<MRT_ColumnDef<ContentSettingV2<Exercise>>[]>(
    () => [
      {
        header: 'Exercise',
        Cell: ({ row }) => (
          <ThumbnailView
            fullImageUrl={row.original.content.fullImageUrl}
            rowView={true}
          />
        ),
        size: 20,
      },
      {
        header: 'Title',
        accessorFn: (row) => row.content.title,
      },
      {
        header: 'Assigned Date',
        accessorFn: (row) => dayjs(row?.schedules[0].startDate).format('MM/DD/YYYY'),
        size: 10,
      },
      {
        header: 'Actions',
        accessorFn: (row) => (
          <ActionsFlexBox
            msg={'exercise'}
            deleteOnClick={() => handleDeleteExerciseOnClick(row.id)}
            editOnClick={() => handleEditExerciseOnClick(row)}
            completeOnClick={() => handleCompleteOnClick(row.id)}
            reportOnClick={() => {
              navigate(ExerciseRouteMap.REPORT, row.id.toString());
            }}
            actionButtons={{
              deleteButton: false,
              editButton: true,
              reportButton: true,
              completeButton: true,
            }}
          />
        ),
        size: 10,
      },
    ],
    [],
  );

  return (
    <>
      {exercises && !isExerciseLoading ? (
        <PatientProfileTable
          columns={columns}
          data={activePatientExercisesById}
          onAddClick={handleOnAddClick}
          title={'Exercises'}
        />
      ) : null}
      <AddExerciseToPatientProfileModal
        open={open}
        onClose={handleCloseModal}
        patientId={patientId}
      />
      {editExercise ? (
        <EditExerciseToPatientProfileModal
          open={editExercise !== undefined}
          initValue={editExercise}
          onClose={() => setEditExercise(undefined)}
          label={editExerciseLabel}
        />
      ) : null}
    </>
  );
};
